import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default api;

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  getVerificationStatus: () => api.get('/auth/verification-status'),
  getMe: () => api.get('/me'),
};

// Mood API
export const moodAPI = {
  create: (data) => api.post('/moods', data),
  getAll: (days = 30) => api.get(`/moods?days=${days}`),
  getSummary: (period = 'week') => api.get(`/moods/summary?period=${period}`),
};

// Symptoms API
export const symptomsAPI = {
  create: (data) => api.post('/symptoms/entries', data),
  getAll: (days = 30) => api.get(`/symptoms/entries?days=${days}`),
};

// Medications API
export const medicationsAPI = {
  create: (data) => api.post('/medications', data),
  getAll: () => api.get('/medications'),
  logAdherence: (id, data) => api.post(`/medications/${id}/adherence`, data),
};

// Prescriptions API
export const prescriptionsAPI = {
  create: (data) => api.post('/prescriptions', data),
  getAll: () => api.get('/prescriptions'),
  verify: (id) => api.post(`/prescriptions/${id}/verify`),
};

// Appointments API
export const appointmentsAPI = {
  create: (data) => api.post('/appointments', data),
  getAll: () => api.get('/appointments'),
  update: (id, status) => api.patch(`/appointments/${id}`, { status }),
};

// Emergency API
export const emergencyAPI = {
  getNumbers: (country = 'US') => api.get(`/emergency/numbers?country=${country}`),
  logEvent: (data) => api.post('/emergency/events', data),
};

// Talk API
export const talkAPI = {
  getAvailability: () => api.get('/talk/availability'),
  setAvailability: (available, language = 'en') => api.post('/talk/availability', { available, language }),
  requestTalk: (language = 'en') => api.post('/talk/request', { language }),
  getPendingMatches: () => api.get('/talk/matches/pending'),
  acceptMatch: (matchId) => api.post(`/talk/matches/${matchId}/accept`),
  declineMatch: (matchId) => api.post(`/talk/matches/${matchId}/decline`),
  getMessages: (threadId, limit = 50) => api.get(`/talk/threads/${threadId}/messages?limit=${limit}`),
  reportThread: (threadId, reason) => api.post(`/talk/threads/${threadId}/report`, { reason }),
};

// Store API
export const storeAPI = {
  getProducts: () => api.get('/store/products'),
  getProduct: (id) => api.get(`/store/products/${id}`),
  createProduct: (data) => api.post('/store/products', data),
  updateProduct: (id, data) => api.put(`/store/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/store/products/${id}`),
  createOrder: (productId, quantity) => api.post('/billing/store/create-order', { product_id: productId, quantity }),
  verifyPayment: (data) => api.post('/billing/store/verify-payment', data),
  getOrders: () => api.get('/store/orders'),
};

// Billing API (Razorpay)
export const billingAPI = {
  getPlans: () => api.get('/billing/plans'),
  createOrder: (plan, discountCode = null) => api.post('/billing/create-order', { plan, discount_code: discountCode }),
  verifyPayment: (data) => api.post('/billing/verify-payment', data),
  getSubscription: () => api.get('/billing/subscription'),
  cancelSubscription: () => api.post('/billing/cancel-subscription'),
  validateDiscount: (code, plan) => api.post('/billing/validate-discount', { code, plan }),
  getDiscountCodes: () => api.get('/billing/discount-codes'),
  createDiscountCode: (data) => api.post('/billing/discount-codes', data),
  deleteDiscountCode: (codeId) => api.delete(`/billing/discount-codes/${codeId}`),
  getOrderHistory: () => api.get('/billing/order-history'),
  downloadInvoice: (orderId) => api.get(`/billing/invoice/${orderId}`, { responseType: 'blob' }),
  emailInvoice: (orderId) => api.post(`/billing/invoice/${orderId}/email`),
};

// AI API
export const aiAPI = {
  chatWithCompanion: (message) => api.post('/ai/companion', { message }),
  getJournalPrompts: () => api.get('/ai/journal-prompts'),
  getCopingStrategies: (emotion) => api.get(`/ai/coping-strategies?emotion=${emotion}`),
};

// Admin API
export const adminAPI = {
  getUsers: (query = '', limit = 100) => api.get(`/admin/users?query=${query}&limit=${limit}`),
  createUser: (userData) => api.post('/admin/users', userData),
  updateUser: (userId, updates) => api.patch(`/admin/users/${userId}`, updates),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  updateUserPermission: (userId, feature, granted) => api.put(`/admin/users/${userId}/permissions`, { feature, granted }),
  getUserChats: (userId) => api.get(`/admin/users/${userId}/chats`),
  getAllChats: (limit = 100) => api.get(`/admin/chats?limit=${limit}`),
  getFlags: (status = 'pending') => api.get(`/admin/moderation/flags?status=${status}`),
  getChatArchive: (threadId) => api.get(`/admin/chat-archives/${threadId}`),
  moderateContent: (flagId, action, notes) => api.post('/admin/moderation/actions', { flag_id: flagId, action, notes }),
  getPendingPrescriptions: () => api.get('/admin/prescriptions/pending'),
  rejectPrescription: (id, reason) => api.post(`/admin/prescriptions/${id}/reject`, { reason }),
  getUrgentConcerns: (status = 'pending') => api.get(`/amigo/admin/urgent-concerns?status=${status}`),
  updateUrgentConcern: (concernId, data) => api.patch(`/amigo/admin/urgent-concerns/${concernId}`, data),
};

// Doctors API
export const doctorsAPI = {
  getAll: () => api.get('/doctors'),
  create: (data) => api.post('/doctors', data),
  delete: (id) => api.delete(`/doctors/${id}`),
};

// Referral API
export const referralAPI = {
  getCode: () => api.get('/referral/code'),
  getStats: () => api.get('/referral/stats'),
  applyCode: (code) => api.post('/referral/apply', { referral_code: code }),
  validateCode: (code) => api.post('/referral/validate', { referral_code: code }),
  getCredits: () => api.get('/referral/credits'),
  useCredits: (amount) => api.post('/referral/use-credits', { amount }),
};

// Calendar Integration API
export const calendarAPI = {
  getConnections: () => api.get('/calendar/connections'),
  getGoogleAuthUrl: () => api.get('/calendar/google/auth-url'),
  disconnectGoogle: () => api.post('/calendar/google/disconnect'),
  getMicrosoftAuthUrl: () => api.get('/calendar/microsoft/auth-url'),
  disconnectMicrosoft: () => api.post('/calendar/microsoft/disconnect'),
  exportAppleCalendar: (days = 30) => api.get(`/calendar/apple/export?days=${days}`, { responseType: 'blob' }),
  createEvent: (event, provider = 'all') => api.post(`/calendar/events/create?provider=${provider}`, event),
  syncCalendar: (provider, syncMedications = true, syncAppointments = true) => 
    api.post('/calendar/sync', { provider, sync_medications: syncMedications, sync_appointments: syncAppointments }),
};

// Push Notification API
export const notificationAPI = {
  getVapidPublicKey: () => api.get('/notifications/vapid-public-key'),
  subscribe: (subscription) => api.post('/notifications/subscribe', subscription),
  unsubscribe: (subscription) => api.post('/notifications/unsubscribe', subscription),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (preferences) => api.put('/notifications/preferences', preferences),
  sendTestNotification: (title, body) => api.post('/notifications/send', { title, body, type: 'general' }),
  getScheduled: () => api.get('/notifications/scheduled'),
  getHistory: (limit = 20) => api.get(`/notifications/history?limit=${limit}`),
};

// MindPulse AI Analysis API
export const mindpulseAPI = {
  getStatus: () => api.get('/mindpulse/status'),
  getAIAnalysis: () => api.get('/mindpulse/ai-analysis'),
  refreshAnalysis: () => api.post('/mindpulse/refresh-analysis'),
  getTemplates: () => api.get('/mindpulse/templates'),
  startSession: (templateId) => api.post('/mindpulse/sessions/start', { template_id: templateId }),
  completeSession: (sessionId, feedback) => api.post('/mindpulse/sessions/complete', { session_id: sessionId, feedback }),
  getHistory: (limit = 10) => api.get(`/mindpulse/history?limit=${limit}`),
};

// Amigo AI Companion API
export const amigoAPI = {
  checkAccess: () => api.get('/amigo/access'),
  sendMessage: (message) => api.post('/amigo/chat', { message }),
  getHistory: (limit = 50) => api.get(`/amigo/history?limit=${limit}`),
  clearHistory: () => api.delete('/amigo/history'),
};